import React, { useEffect, useState } from 'react'
import messages from './messages'
import {
  DataTable,
  Button,
  TextArea,
} from "@arnsbogroup/react-voicearchive"
import useSyntheticVoice from "hooks/useSyntheticVoice"
import { FormattedMessage, useIntl } from 'react-intl'

const AiVoiceLines = ({ attribute, instance, onChange }) => {
  const { value: aiVoiceLines, name, disabled } = attribute
  const { aivoicetalent: aiVoiceTalent } = instance
  const { formatMessage } = useIntl()
  const emptySentence = {
    line: '',
    audio_clip_url: null,
    deleted: false
  }

  const [syntheticVoiceState, scriptToVoice] = useSyntheticVoice()
  const [loading, setLoading] = useState(false)
  const [syntheticVoice, setSyntheticVoice] = useState(null)
  const [sentences, setSentences] = useState([])
  const [sentence, setSentence] = useState("")
  const [selectedVoiceLines, setSelectedVoiceLines] = useState([])

  useEffect(() => {
    if (aiVoiceTalent) {
      getSyntheticVoice()
    }
  }, [aiVoiceTalent])

  const getSyntheticVoice = () => {
    fetch(
      `/rest/v2/supplier/${aiVoiceTalent}/synthetic-voice`,
      { credentials: "same-origin" }
    )
      .then((response) => response.json())
      .then((syntheticVoice) => {
        if (syntheticVoice.data.length > 0) {
          setSyntheticVoice(syntheticVoice.data[0])
        }
      })
  }

  useEffect(() => {
    if (aiVoiceLines) {
      setSentences(aiVoiceLines)
      setSelectedVoiceLines(aiVoiceLines.filter((voiceLine) => voiceLine.selected).map(({audio_clip_url}) => audio_clip_url))
    }
  }, [aiVoiceLines])

  useEffect(() => {
    if (sentences && selectedVoiceLines && !!selectedVoiceLines.length) {
      onChange(
        sentences.map((sentence, index) => ({
          ...sentence,
          selected: selectedVoiceLines.includes(sentence.audio_clip_url)
        }))
      )
    }
  }, [selectedVoiceLines, sentences])

  const addSentenceHandler = async () => {
    if (sentence !== '') {
      const url = await onGenerate(sentence)
      const newEntry = { ...emptySentence, line: sentence, audio_clip_url: url }
      setSentences((prev) => [...prev, newEntry])
      setSentence("")
    }
  }

  const onGenerate = async (line) => {
    setLoading(true)
    const url = await scriptToVoice(syntheticVoice.elevenlabs_voice_id, line)
    setLoading(false)
    return url
  }

  const onDelete = (entry) => {
    if (confirm(formatMessage(messages.deleteConfirm))) {
      const updatedSentences = sentences.map((voiceLine) =>
        voiceLine.audio_clip_url == entry.audio_clip_url ? { ...voiceLine, deleted: true } : voiceLine
      )
      setSentences(updatedSentences)
    }
  }

  const onRegenerate = async (entry) => {
      const newUrl = await onGenerate(entry.line)
      const updatedSentences = sentences.map((voiceLine) =>
        voiceLine.audio_clip_url == entry.audio_clip_url ? { ...voiceLine, audio_clip_url: newUrl } : voiceLine
      )
      setSentences(updatedSentences)
  }

  const onListen = (entry) => {
    let url = null
    if (entry?.audio_clip_url) {
      url = entry.audio_clip_url
      const audio = new Audio(url)
      audio.play()
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      addSentenceHandler()
    }
  }

  return (
    <fieldset className="space-y-1">
      <p className="text-xs font-medium text-gray-600 uppercase tracking-wider">
        { name }
      </p>

        <div className="flex gap-4 my-4">
          <div className='w-full'>
            <TextArea
              placeholder={formatMessage(messages.voiceLine)}
              value={sentence}
              disabled={!syntheticVoice || disabled || loading}
              onChange={({ target: { value } }) => {
                setSentence(value)
              }}
              onKeyDown={handleKeyDown}
            />
          </div>

          <div className="my-auto space-x-2">
            <Button
              size="sm"
              variant='outline'
              onClick={() => addSentenceHandler()}
              disabled={!syntheticVoice || disabled || sentence == '' || loading}
            >
              {!loading ? <FormattedMessage {...messages.addLine} /> : <FormattedMessage {...messages.generating} />}
            </Button>       
          </div>
        </div>
        <DataTable
          rowsSelected={selectedVoiceLines}
          rowSelectionKey="audio_clip_url"
          onRowSelected={(audio_clip_url) => {
            const indexToRemove = selectedVoiceLines.findIndex(voiceLine =>
              voiceLine == audio_clip_url
            )
            if (indexToRemove !== -1) {
              setSelectedVoiceLines(selectedVoiceLines.filter((voiceLine, index) => index !== indexToRemove))
            } else {
              setSelectedVoiceLines((prev) => [...prev, audio_clip_url])
            }
          }}
          noDataMessage={formatMessage(messages.noVoiceLines)}
          head={{
            line: { label: formatMessage(messages.tableHeaderVoiceLine) },
            listen: { label: " ", className: "w-64 text-right"},
          }}
          body={{
            line: ( {children} ) => <div className="whitespace-normal"><p>{children}</p></div>,
            listen: (entry) => (
              <>
                <Button
                  size="xs"
                  icon={['fa', 'trash']}
                  state="negative"
                  disabled={disabled || loading}
                  onClick={(e) => {
                    e.stopPropagation()
                    onDelete(entry)
                  }}
                  className="mx-1"
                >
                  <FormattedMessage {...messages.delete} />
                </Button>
                <Button
                  size="xs"
                  icon={['fa', 'redo']}
                  variant="outline"
                  disabled={disabled || loading}
                  onClick={(e) => {
                    e.stopPropagation()
                    onRegenerate(entry)
                  }}
                  className="mx-1"
                >
                  <FormattedMessage {...messages.reGenerate} />
                </Button>
                <Button
                  size="xs"
                  icon={['fa', 'play']}
                  variant="outline"
                  disabled={!entry?.audio_clip_url || loading}
                  onClick={(e) => {
                    e.stopPropagation()
                    onListen(entry)
                  }}
                  className="mx-1"
                >
                  <FormattedMessage {...messages.listen} />
                </Button>
              </>
            ),
          }}
          entries={sentences.filter(({deleted}) => !(+deleted))}
        />
    </fieldset>
  )
}

export default AiVoiceLines
